import { FeatureFlag, useCurrentAccount } from '@alliance-disposal/client';
import { FeatureFlagKeys } from '@alliance-disposal/transport-types';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { twMerge } from 'tailwind-merge';
import { useRestrictedAccess } from '../../hooks/use-restricted-access';
import { DispatchTaskTypes, buildDispatchPath } from '../../pages/dispatch/utils/dispatch-params';
import { useUnifiedDispatchTaskTypes } from '../../pages/dispatch/utils/use-unified-dispatch-task-types';
import routes, { getRfpListPath } from '../../utils/routes';
import ExpandingBlock from './components/expanding-block';

/**
 * Side navigation bar on desktop ( > 1024px) displays
 */
const Toolbar = ({
    desktopToolBarOpen: forceToolbarOpen,
    isDesktop,
    toggleOpen,
    featureFlags,
}: {
    isDesktop: boolean;
    desktopToolBarOpen: boolean;
    toggleOpen?: () => void;
    featureFlags?: FeatureFlag[];
}) => {
    const { hauler } = useCurrentAccount();
    const { isBillingUser, isDispatchUser } = useRestrictedAccess();
    const liftlineEnabled = useFeatureFlagEnabled(FeatureFlagKeys.LIFTLINE) === true;
    const unifiedBillingEnabled = useFeatureFlagEnabled(FeatureFlagKeys.UNIFIED_BILLING) === true;
    // Same source as the /dispatch route guard and the legacy route redirects, so the nav can't link a hauler to a
    // dispatch page they'd be redirected away from. Per task type: a hauler can be on the unified portable toilet
    // dispatch while their roll-off schedules are still legacy. An unresolved flag keeps the legacy links, which the
    // redirect then honours by rendering the legacy page.
    const unifiedDispatchTaskTypes = useUnifiedDispatchTaskTypes();
    const rollOffDispatchUnified = unifiedDispatchTaskTypes?.includes(DispatchTaskTypes.ROLL_OFF) ?? false;
    const portableToiletDispatchUnified = unifiedDispatchTaskTypes?.includes(DispatchTaskTypes.PORTABLE_TOILET) ?? false;
    const desktopToolBarOpen = forceToolbarOpen;

    const navigateSideEffect = () => {
        if (!isDesktop) {
            toggleOpen?.();
        }
    };

    return (
        <nav
            className={`${
                desktopToolBarOpen ? 'w-full md:w-[10.25rem]' : 'w-0 md:mr-4 md:w-2'
            } group/nav relative flex flex-none flex-col overflow-visible transition-all duration-200 ease-in`}
        >
            {/* collapse button */}
            <div
                className={twMerge(
                    'absolute bottom-0 right-0 top-0 hidden bg-slate-200 transition-all duration-200 ease-in md:block',
                    desktopToolBarOpen ? 'ml-2 w-0' : '-right-2 w-0.5',
                )}
            >
                <div
                    className={twMerge(
                        'hover:bg-primary-400 group absolute right-1/2 top-20 z-50 hidden size-6 translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_rgba(9,30,66,0.08),0_2px_4px_1px_rgba(9,30,66,0.08)] transition-all duration-200 ease-in-out hover:size-7 group-hover/nav:opacity-100 md:flex',
                        forceToolbarOpen ? '-rotate-90 md:opacity-0' : 'rotate-90',
                    )}
                    onClick={toggleOpen}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="size-6 transform text-zinc-700 transition-all duration-200 ease-in-out hover:scale-110 group-hover:text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                </div>
            </div>

            <div
                className={`bg-main-sidebar w-full flex-1 flex-col gap-y-8 overflow-hidden overflow-y-auto whitespace-nowrap py-8 ${
                    desktopToolBarOpen ? 'flex' : 'hidden'
                }`}
            >
                {/* <div
                    className={classItemBlock}
                    onClick={() => {
                        navigateSideEffect();
                        // history(routes.leads.create);
                    }}
                >
                    Create lead
                </div> */}

                <ExpandingBlock
                    initiallyExpanded={true}
                    onNavigate={navigateSideEffect}
                    heading="Roll Off"
                    featureFlags={featureFlags}
                    routeList={[
                        ...(featureFlags?.includes('wayste-standard')
                            ? [
                                  {
                                      text: 'Create order',
                                      path: routes.operationsRollOff.create,
                                      featureFlag: 'wayste-operations-rolloff' as const,
                                      hidden: isBillingUser,
                                  },
                              ]
                            : []),
                        {
                            text: 'Orders',
                            path: routes.orders.list,
                        },
                        {
                            text: 'Schedules',
                            path: rollOffDispatchUnified ? buildDispatchPath(DispatchTaskTypes.ROLL_OFF) : routes.roScheduling.list,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser,
                        },
                    ]}
                />

                <ExpandingBlock
                    initiallyExpanded={true}
                    onNavigate={navigateSideEffect}
                    featureFlags={featureFlags}
                    heading="Recurring"
                    badge="Beta"
                    routeList={[
                        {
                            text: 'Dashboard',
                            path: routes.dispatching.dashboard,
                            featureFlag: 'wayste-commercial',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Subscriptions',
                            path: routes.subscriptions.list,
                            featureFlag: 'wayste-commercial',
                        },
                        {
                            text: 'Routes',
                            path: routes.dispatching.commercialRoutes.list,
                            featureFlag: 'wayste-commercial',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Driver Schedules',
                            path: routes.dispatching.schedule,
                            featureFlag: 'wayste-commercial',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Container Delivery',
                            path: routes.dispatching.containerDelivery,
                            featureFlag: 'wayste-commercial',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Learn More',
                            path: routes.commercialPreview,
                            featureFlag: !featureFlags?.includes('wayste-commercial'),
                        },
                    ]}
                />
                <ExpandingBlock
                    initiallyExpanded={true}
                    onNavigate={navigateSideEffect}
                    heading="Portable Toilets"
                    featureFlags={featureFlags}
                    routeList={[
                        {
                            text: 'Rentals',
                            path: routes.portableToilets.list,
                            featureFlag: 'wayste-portable-toilet',
                        },
                        {
                            text: 'Dispatch',
                            path: portableToiletDispatchUnified
                                ? buildDispatchPath(DispatchTaskTypes.PORTABLE_TOILET)
                                : routes.portableToilets.ptDispatch,
                            featureFlag: 'wayste-portable-toilet',
                            hidden: isBillingUser,
                        },
                    ]}
                />

                <ExpandingBlock
                    initiallyExpanded={true}
                    onNavigate={navigateSideEffect}
                    heading="Operations"
                    featureFlags={featureFlags}
                    routeList={[
                        {
                            text: 'Containers',
                            path: routes.equipment.containers,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Container Map',
                            path: routes.equipment.map,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Inventory',
                            path: routes.equipment.inventory,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Tracking',
                            path: routes.driverTracking.list,
                            featureFlag: 'wayste-timetracking',
                            hidden: isBillingUser,
                        },
                        {
                            text: 'Vehicles',
                            path: routes.equipment.vehicles,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser,
                            // featureFlag: 'wayste-commercial', //TODO: Change to wayste-standard
                        },
                    ]}
                />

                <ExpandingBlock
                    initiallyExpanded={true}
                    onNavigate={navigateSideEffect}
                    featureFlags={featureFlags}
                    heading="Back Office"
                    routeList={[
                        {
                            text: 'Billing',
                            path: routes.billing.list,
                            hidden: isDispatchUser,
                        },
                        {
                            text: 'Billing V2',
                            path: routes.billingV2.invoices,
                            featureFlag: unifiedBillingEnabled && (featureFlags?.includes('unified-billing') ?? false),
                            hidden: isDispatchUser,
                        },
                        {
                            text: 'Pricing',
                            path: routes.pricing.list,
                            hidden: isBillingUser || isDispatchUser,
                        },
                        {
                            text: 'Time Sheets',
                            path: routes.timeSheet.list,
                            featureFlag: 'wayste-timetracking',
                        },
                        {
                            text: 'Reports',
                            path: routes.reports.list,
                            featureFlag: 'wayste-standard',
                            hidden: isBillingUser || isDispatchUser,
                        },
                        {
                            text: 'Customers',
                            path: routes.customers.list,
                            featureFlag: 'wayste-standard',
                        },
                        {
                            text: 'Task Board',
                            path: routes.taskBoard,
                            featureFlag: 'wayste-standard',
                        },
                        {
                            text: 'ReadyPay',
                            path: routes.liftline.list,
                            featureFlag: liftlineEnabled && (featureFlags?.includes('ready-pay') ?? false),
                            hidden: isDispatchUser,
                        },
                    ]}
                />

                {hauler.isSourgumPartner && (
                    <ExpandingBlock
                        initiallyExpanded={true}
                        onNavigate={navigateSideEffect}
                        featureFlags={featureFlags}
                        heading="Partner Connect"
                        routeList={[
                            {
                                text: 'Partner Settings',
                                path: routes.partnerPortalConnect.list,
                                featureFlag: 'sourgum-marketplace-partner',
                            },
                            {
                                text: 'Payouts',
                                path: routes.partnerPortalConnect.payouts,
                                featureFlag: 'sourgum-marketplace-partner',
                                hidden: isDispatchUser,
                            },
                            {
                                text: 'RFP',
                                path: getRfpListPath(),
                                featureFlag: 'sourgum-marketplace-partner',
                            },
                        ]}
                    />
                )}
            </div>
        </nav>
    );
};

export default Toolbar;
